import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { softphoneService } from '@/api/services/softphoneService'
import { MUTATION_KEYS, QUERY_KEYS } from '@/config/api'
import { createApiRetryHandler } from '@/utils/apiRetry'
import { useGetAllDispatchers } from '@/hooks/api/useDispatcher'
import type {
  NormalizedCallerHistoryEntry,
  SoftphoneHistoryParams,
  SoftphoneStartCallRequest,
} from '@/types/api/softphone'

export const SOFTPHONE_QUERY_KEYS = {
  all: QUERY_KEYS.softphone.all,
  credentials: () => [...QUERY_KEYS.softphone.all, 'credentials'] as const,
  activeCalls: (limit: number) =>
    [...QUERY_KEYS.softphone.all, 'active', limit] as const,
  history: (params: SoftphoneHistoryParams) =>
    [...QUERY_KEYS.softphone.all, 'history', params] as const,
  callerHistory: (phoneNumber: string, actorId?: string, limit = 50) =>
    [
      ...QUERY_KEYS.softphone.all,
      'caller-history',
      phoneNumber,
      actorId,
      limit,
    ] as const,
  call: (callId: string) =>
    [...QUERY_KEYS.softphone.all, 'call', callId] as const,
  statistics: () => [...QUERY_KEYS.softphone.all, 'statistics'] as const,
  health: () => [...QUERY_KEYS.softphone.all, 'health'] as const,
  queueStatus: (dispatcherId?: string) =>
    [...QUERY_KEYS.softphone.all, 'queue-status', dispatcherId] as const,
}

export const useSoftphoneCredentials = (enabled = true) => {
  return useQuery({
    queryKey: SOFTPHONE_QUERY_KEYS.credentials(),
    queryFn: () => softphoneService.getCredentials(),
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: createApiRetryHandler(1),
  })
}

export const useActiveSoftphoneCalls = (limit = 50, enabled = true) => {
  return useQuery({
    queryKey: SOFTPHONE_QUERY_KEYS.activeCalls(limit),
    queryFn: () => softphoneService.listActiveCalls(limit),
    enabled,
    staleTime: 15 * 1000,
    gcTime: 60 * 1000,
    refetchInterval: enabled ? 30 * 1000 : false,
    retry: createApiRetryHandler(1),
  })
}

export const useSoftphoneCallHistory = (
  params: SoftphoneHistoryParams = {},
  enabled = true
) => {
  return useQuery({
    queryKey: SOFTPHONE_QUERY_KEYS.history(params),
    queryFn: () => softphoneService.getCallHistory(params),
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: enabled ? 30 * 1000 : false,
    retry: createApiRetryHandler(1),
  })
}

const isPhoneNumberReadyForLookup = (phoneNumber?: string) => {
  if (!phoneNumber) return false
  const digits = phoneNumber.replace(/\D/g, '')
  return digits.length >= 10
}

const titleCaseLabel = (value?: string) => {
  if (!value) return undefined
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

const buildDispatcherNameMap = (
  dispatchersData: unknown
): Map<string, string> => {
  const lookup = new Map<string, string>()
  if (!dispatchersData || typeof dispatchersData !== 'object') return lookup

  const data = dispatchersData as {
    data?: { dispatchers?: Array<Record<string, unknown>> }
    dispatchers?: Array<Record<string, unknown>>
    items?: Array<Record<string, unknown>>
  }

  const dispatchers =
    data.data?.dispatchers || data.dispatchers || data.items || []

  dispatchers.forEach(item => {
    const id =
      (typeof item.dispatcher_id === 'string' && item.dispatcher_id) ||
      (typeof item.user_id === 'string' && item.user_id) ||
      ''
    if (!id) return

    const first =
      typeof item.first_name === 'string' ? item.first_name.trim() : ''
    const last = typeof item.last_name === 'string' ? item.last_name.trim() : ''
    const combined = `${first} ${last}`.trim()
    const email = typeof item.email === 'string' ? item.email.trim() : ''
    const name = combined || email
    if (name) lookup.set(id, name)
  })

  return lookup
}

const resolveCallerHistoryEntries = (
  entries: NormalizedCallerHistoryEntry[],
  dispatcherNameById: Map<string, string>
): NormalizedCallerHistoryEntry[] => {
  return entries.map(entry => {
    if (entry.dispatcherName) return entry

    const resolvedName = entry.dispatcherId
      ? dispatcherNameById.get(entry.dispatcherId)
      : undefined

    const actorRole =
      typeof entry.raw.actor_role === 'string'
        ? titleCaseLabel(entry.raw.actor_role)
        : undefined

    return {
      ...entry,
      dispatcherName: resolvedName || actorRole,
    }
  })
}

export const useCallerHistory = (
  phoneNumber?: string,
  actorId?: string,
  options?: { enabled?: boolean; limit?: number }
) => {
  const limit = options?.limit ?? 50
  const normalizedPhone = phoneNumber?.replace(/[^\d+]/g, '') || ''
  const enabled =
    options?.enabled !== false && isPhoneNumberReadyForLookup(normalizedPhone)

  const { data: dispatchersData } = useGetAllDispatchers()
  const dispatcherNameById = useMemo(
    () => buildDispatcherNameMap(dispatchersData),
    [dispatchersData]
  )

  const query = useQuery({
    queryKey: SOFTPHONE_QUERY_KEYS.callerHistory(
      normalizedPhone,
      actorId,
      limit
    ),
    queryFn: () =>
      softphoneService.getCallerHistory({
        phone_number: normalizedPhone,
        actor_id: actorId,
        limit,
      }),
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: createApiRetryHandler(1),
  })

  const entries = useMemo(
    () =>
      resolveCallerHistoryEntries(
        query.data?.entries || [],
        dispatcherNameById
      ),
    [query.data?.entries, dispatcherNameById]
  )

  return {
    ...query,
    data: query.data
      ? {
          ...query.data,
          entries,
        }
      : query.data,
  }
}

export const useSoftphoneCall = (callId?: string) => {
  return useQuery({
    queryKey: SOFTPHONE_QUERY_KEYS.call(callId || ''),
    queryFn: () => softphoneService.getCall(callId!),
    enabled: Boolean(callId),
    staleTime: 10 * 1000,
    gcTime: 2 * 60 * 1000,
    retry: createApiRetryHandler(1),
  })
}

export const useSoftphoneStatistics = (enabled = true) => {
  return useQuery({
    queryKey: SOFTPHONE_QUERY_KEYS.statistics(),
    queryFn: () => softphoneService.getStatistics(),
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: createApiRetryHandler(1),
  })
}

export const useSoftphoneHealth = (enabled = false) => {
  return useQuery({
    queryKey: SOFTPHONE_QUERY_KEYS.health(),
    queryFn: () => softphoneService.getHealth(),
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: createApiRetryHandler(1),
  })
}

export const useSoftphoneQueueStatus = (
  dispatcherId?: string,
  enabled = true
) => {
  return useQuery({
    queryKey: SOFTPHONE_QUERY_KEYS.queueStatus(dispatcherId),
    queryFn: () => softphoneService.getQueueStatus(dispatcherId),
    enabled,
    staleTime: 10 * 1000,
    gcTime: 60 * 1000,
    refetchInterval: enabled ? 15 * 1000 : false,
    retry: createApiRetryHandler(1),
  })
}

const invalidateSoftphoneActivityQueries = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  queryClient.invalidateQueries({
    predicate: query => {
      const [scope, type] = query.queryKey
      return scope === SOFTPHONE_QUERY_KEYS.all[0] && type !== 'credentials'
    },
  })
}

export const useStartSoftphoneCall = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEYS.SOFTPHONE_START_CALL],
    mutationFn: ({
      payload,
      idempotencyKey,
    }: {
      payload: SoftphoneStartCallRequest
      idempotencyKey: string
    }) => softphoneService.startUserCall(payload, idempotencyKey),
    onSuccess: () => {
      invalidateSoftphoneActivityQueries(queryClient)
    },
    onError: (error: any) => {
      toast.error(error.message || 'Unable to start call')
    },
  })
}

export const useSoftphoneCallControl = () => {
  const queryClient = useQueryClient()

  const invalidateSoftphone = () => {
    invalidateSoftphoneActivityQueries(queryClient)
  }

  const accept = useMutation({
    mutationKey: [MUTATION_KEYS.SOFTPHONE_ACCEPT_CALL],
    mutationFn: (callId: string) => softphoneService.acceptCall(callId),
    onSuccess: invalidateSoftphone,
  })

  const reject = useMutation({
    mutationKey: [MUTATION_KEYS.SOFTPHONE_REJECT_CALL],
    mutationFn: ({ callId, reason }: { callId: string; reason?: string }) =>
      softphoneService.rejectCall(callId, reason),
    onSuccess: invalidateSoftphone,
  })

  const hangup = useMutation({
    mutationKey: [MUTATION_KEYS.SOFTPHONE_HANGUP_CALL],
    mutationFn: (callId: string) => softphoneService.hangUpCall(callId),
    onSuccess: invalidateSoftphone,
  })

  const hold = useMutation({
    mutationKey: [MUTATION_KEYS.SOFTPHONE_HOLD_CALL],
    mutationFn: (callId: string) => softphoneService.holdCall(callId),
    onSuccess: invalidateSoftphone,
  })

  const unhold = useMutation({
    mutationKey: [MUTATION_KEYS.SOFTPHONE_UNHOLD_CALL],
    mutationFn: (callId: string) => softphoneService.unholdCall(callId),
    onSuccess: invalidateSoftphone,
  })

  return {
    accept,
    reject,
    hangup,
    hold,
    unhold,
  }
}
